/**
 * ExecutionTimeline — timeline visualization of log entries.
 *
 * Renders log entries as dots/bars on a horizontal time axis,
 * grouped by source (System, Plugin, Engine, Sidecar …).
 * Clicking an entry opens a detail panel on the right.
 */

import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { JsonTree } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { cn } from "../lib/utils";
import {
    type LogEntry,
    type LogSource,
    useLogStore,
} from "../store/log-store";
import {
    getThemeAccentStyle,
    type ThemeAccent,
} from "../theme/runtime/theme-accents";

/* ── Source display config ── */
const sourceConfig: Record<LogSource, { labelKey: string; accent: ThemeAccent }> = {
    system: { labelKey: "log.source.system", accent: "muted" },
    plugin: { labelKey: "log.source.plugin", accent: "success" },
    extension: { labelKey: "log.source.extension", accent: "render" },
    parser: { labelKey: "log.source.parser", accent: "for" },
    resolver: { labelKey: "log.source.resolver", accent: "refScope" },
    ui: { labelKey: "log.source.ui", accent: "content" },
    sidecar: { labelKey: "log.source.sidecar", accent: "warning" },
    engine: { labelKey: "log.source.engine", accent: "info" },
    transport: { labelKey: "log.source.transport", accent: "render" },
    mcp: { labelKey: "log.source.mcp", accent: "warning" },
};

const levelBorder: Record<string, string> = {
    error: "ring-2 ring-red-500",
    warn: "ring-1 ring-amber-400",
};

/* ── Format timestamp ── */
function fmtTime(iso: string): string {
    const d = new Date(iso);
    return (
        d.toLocaleTimeString("en-GB", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }) +
        "." +
        String(d.getMilliseconds()).padStart(3, "0")
    );
}

/* ── Detail panel (right side) ── */
function DetailPanel({ entry, onClose }: { entry: LogEntry; onClose: () => void }) {
    const t = useT("editor");
    return (
        <div className="w-[380px] shrink-0 border-l border-border bg-muted/10 flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between px-2 py-1 border-b border-border/50">
                <span className="text-[11px] font-semibold text-foreground/80 truncate">{t("log.detail")}</span>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                >
                    <X size={12} />
                </button>
            </div>

            <div className="flex-1 overflow-auto p-2 space-y-2 text-[11px]">
                {/* Time */}
                <Field label={t("log.field.time")} value={fmtTime(entry.timestamp)} />
                {/* Level */}
                <Field label={t("log.field.level")}>
                    <span
                        className="px-1.5 py-0.5 rounded text-[9px] font-medium theme-accent-chip"
                        style={getThemeAccentStyle(
                            entry.level === "error"
                                ? "error"
                                : entry.level === "warn"
                                    ? "warning"
                                    : entry.level === "info"
                                        ? "info"
                                        : "muted",
                        )}
                    >
                        {entry.level.toUpperCase()}
                    </span>
                </Field>
                {/* Source */}
                <Field label={t("log.field.source")}>
                    <span className="flex items-center gap-1">
                        <span
                            className="w-2 h-2 rounded-full theme-accent-fill"
                            style={getThemeAccentStyle(sourceConfig[entry.source].accent)}
                        />
                        {t(sourceConfig[entry.source].labelKey)}
                    </span>
                </Field>
                {/* Definition */}
                {entry.unitId && <Field label={t("log.field.definition")} value={entry.unitId} mono />}
                {/* Plugin */}
                {entry.pluginName && <Field label={t("log.field.plugin")} value={entry.pluginName} mono />}
                {/* Message */}
                <div>
                    <div className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">{t("log.field.message")}</div>
                    <div className="text-foreground/80 whitespace-pre-wrap break-words bg-muted/30 rounded p-1.5 font-mono text-[10px]">
                        {entry.message}
                    </div>
                </div>
                {/* Stack */}
                {entry.stack && (
                    <div>
                        <div className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">{t("log.field.stack")}</div>
                        <pre
                            className="text-[9px] font-mono whitespace-pre-wrap break-all rounded p-1.5 max-h-28 overflow-auto theme-accent-banner"
                            style={getThemeAccentStyle("error")}
                        >
                            {entry.stack}
                        </pre>
                    </div>
                )}
                {/* Data */}
                {entry.data != null && (
                    <div>
                        <div className="text-[9px] text-muted-foreground/60 uppercase mb-0.5">{t("log.field.data")}</div>
                        <div className="bg-muted/30 rounded p-1.5 max-h-28 overflow-auto">
                            <JsonTree value={entry.data} defaultExpandDepth={1} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, mono, children }: { label: string; value?: string; mono?: boolean; children?: React.ReactNode }) {
    return (
        <div className="flex items-baseline gap-2">
            <span className="text-[9px] text-muted-foreground/60 uppercase w-16 shrink-0">{label}</span>
            {children ?? <span className={cn("text-foreground/80", mono && "font-mono")}>{value}</span>}
        </div>
    );
}

/* ── Grouped row: one source type ── */
function SourceLane({
    source,
    entries,
    timelineStart,
    timelineSpan,
    selectedId,
    onSelect,
}: {
    source: LogSource;
    entries: LogEntry[];
    timelineStart: number;
    timelineSpan: number;
    selectedId: number | null;
    onSelect: (entry: LogEntry) => void;
}) {
    const cfg = sourceConfig[source];
    const t = useT("editor");

    return (
        <div className="flex items-center h-7 border-b border-border/20 hover:bg-muted/20">
            {/* Label */}
            <div className="w-[80px] shrink-0 px-2 flex items-center gap-1.5">
                <span
                    className="w-2 h-2 rounded-full shrink-0 theme-accent-fill"
                    style={getThemeAccentStyle(cfg.accent)}
                />
                <span className="text-[10px] text-foreground/70 font-medium truncate">{t(cfg.labelKey)}</span>
            </div>
            {/* Count */}
            <div className="w-[32px] shrink-0 text-[9px] text-muted-foreground/50 text-right pr-1">
                {entries.length}
            </div>
            {/* Timeline strip */}
            <div className="flex-1 relative h-full min-w-0">
                {entries.map((entry) => {
                    const ts = new Date(entry.timestamp).getTime();
                    const leftPct = timelineSpan > 0 ? ((ts - timelineStart) / timelineSpan) * 100 : 50;
                    const isSelected = entry.id === selectedId;

                    return (
                        <button
                            key={entry.id}
                            type="button"
                            className={cn(
                                "absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full transition-all cursor-pointer hover:scale-150",
                                "theme-accent-fill",
                                levelBorder[entry.level] ?? "",
                                isSelected && "scale-150 ring-2 ring-primary",
                            )}
                            style={{
                                ...getThemeAccentStyle(cfg.accent),
                                left: `${Math.max(0.5, Math.min(99, leftPct))}%`,
                            }}
                            title={`[${entry.level}] ${entry.message.slice(0, 80)}`}
                            onClick={() => onSelect(entry)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

/* ── Axis ticks ── */
function TimelineAxis({ timelineStart, timelineSpan }: { timelineStart: number; timelineSpan: number }) {
    const ticks = useMemo(() => {
        if (timelineSpan <= 0) return [];
        const count = 6;
        const result: { pct: number; label: string }[] = [];
        for (let i = 0; i <= count; i++) {
            const epoch = timelineStart + (i / count) * timelineSpan;
            const d = new Date(epoch);
            result.push({
                pct: (i / count) * 100,
                label:
                    d.toLocaleTimeString("en-GB", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }) +
                    "." +
                    String(d.getMilliseconds()).padStart(3, "0"),
            });
        }
        return result;
    }, [timelineStart, timelineSpan]);

    return (
        <div className="flex items-center h-4 border-b border-border/50 bg-muted/20">
            <div className="w-[112px] shrink-0" />
            <div className="flex-1 relative h-full min-w-0">
                {ticks.map((t, i) => (
                    <span
                        key={i}
                        className="absolute top-0 text-[7px] text-muted-foreground/40 font-mono -translate-x-1/2 whitespace-nowrap"
                        style={{ left: `${t.pct}%` }}
                    >
                        {t.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

/* ── Main component ── */
export function ExecutionTimeline() {
    const entries = useLogStore((s) => s.entries);
    const filter = useLogStore((s) => s.filter);
    const getFilteredEntries = useLogStore((s) => s.getFilteredEntries);
    const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);

    // Use filtered entries
    const filtered = useMemo(
        () => getFilteredEntries(),
        [entries, filter, getFilteredEntries],
    );

    // Group by source
    const grouped = useMemo(() => {
        const map = new Map<LogSource, LogEntry[]>();
        for (const entry of filtered) {
            const list = map.get(entry.source);
            if (list) list.push(entry);
            else map.set(entry.source, [entry]);
        }
        return map;
    }, [filtered]);

    // Timeline boundaries
    const { timelineStart, timelineSpan } = useMemo(() => {
        if (filtered.length === 0) return { timelineStart: 0, timelineSpan: 0 };
        let min = Infinity;
        let max = -Infinity;
        for (const e of filtered) {
            const t = new Date(e.timestamp).getTime();
            if (t < min) min = t;
            if (t > max) max = t;
        }
        const span = max - min;
        return { timelineStart: min, timelineSpan: span > 0 ? span * 1.05 : 1000 };
    }, [filtered]);

    // Source order
    const sourceOrder: LogSource[] = ["system", "engine", "plugin", "sidecar", "parser", "resolver", "ui", "mcp"];

    if (filtered.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-[11px] text-muted-foreground/40">
                No log entries yet
            </div>
        );
    }

    return (
        <div className="flex h-full">
            {/* Left: timeline */}
            <div className="flex flex-col flex-1 min-w-0 h-full">
                <TimelineAxis timelineStart={timelineStart} timelineSpan={timelineSpan} />

                <div className="flex-1 overflow-auto">
                    {sourceOrder.map((source) => {
                        const group = grouped.get(source);
                        if (!group || group.length === 0) return null;
                        return (
                            <SourceLane
                                key={source}
                                source={source}
                                entries={group}
                                timelineStart={timelineStart}
                                timelineSpan={timelineSpan}
                                selectedId={selectedEntry?.id ?? null}
                                onSelect={setSelectedEntry}
                            />
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-3 py-0.5 border-t border-border/50 bg-muted/10 text-[10px] text-muted-foreground/50">
                    <span>{filtered.length} entries</span>
                    <span className="ml-auto">{grouped.size} sources</span>
                </div>
            </div>

            {/* Right: detail panel */}
            {selectedEntry && (
                <DetailPanel
                    entry={selectedEntry}
                    onClose={() => setSelectedEntry(null)}
                />
            )}
        </div>
    );
}
